import { useState, useCallback, useEffect } from "react";
import {
  Sun,
  MapPinned,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

export default function SmartFeatures({ onQuoteClick, onCallbackClick }) {
  const { t } = useTranslation();
  const s = t.smart;
  const total = s.landmarks.length;
  const [idx, setIdx] = useState(0);

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(
    () => setIdx((i) => (i - 1 + total) % total),
    [total],
  );

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  // Reset index when language change shrinks/keeps array (defensive)
  useEffect(() => {
    if (idx >= total) setIdx(0);
  }, [idx, total]);

  const active = s.landmarks[idx];

  return (
    <section
      data-testid="smart-features-section"
      className="relative py-16 md:py-32 bg-[#ebe2f5]"
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-24 bg-gradient-to-b from-transparent to-[#dfd3ee]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="max-w-3xl mb-14 md:mb-20">
          <p className="text-eyebrow">{s.eyebrow}</p>
          <h2 className="mt-5 font-serif-display text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a] sm:leading-[1.05]">
            {s.titleA}{" "}
            <span className="italic gold-text">{s.titleB}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {/* Landmarks carousel */}
          <div
            data-testid="landmarks-carousel"
            className="md:col-span-4 relative rounded-3xl overflow-hidden bg-[#f5effb] border border-white/5 min-h-[400px] md:min-h-[520px]"
          >
            {s.landmarks.map((l, i) => (
              <div
                key={i}
                aria-hidden={i !== idx}
                className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${
                  i === idx ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={l.image}
                  alt={l.name}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2a162f]/95 via-[#2a162f]/55 to-transparent" />
              </div>
            ))}

            <div className="relative z-10 flex items-center justify-between p-5 md:p-8">
              <div className="hidden md:flex items-center gap-2">
                <MapPinned size={18} className="text-white" />
                <span className="text-[10px] tracking-[0.32em] uppercase font-semibold text-white/90">{s.label}</span>
              </div>
              <div className="md:hidden">
                <MapPinned size={16} className="text-white" />
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="landmark-prev"
                  onClick={prev}
                  aria-label="prev"
                  className="w-10 h-10 rounded-full glass flex items-center justify-center text-[#1a1a1a] hover:text-[#0a0a0a] hover:border-[#0a0a0a]/50 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  data-testid="landmark-next"
                  onClick={next}
                  aria-label="next"
                  className="w-10 h-10 rounded-full glass flex items-center justify-center text-[#1a1a1a] hover:text-[#0a0a0a] hover:border-[#0a0a0a]/50 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-10 p-5 sm:p-6 md:p-10">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#c5a059]">
                {active.region}
              </div>
              <h3
                data-testid="landmark-name"
                className="mt-2 sm:mt-3 font-serif-display text-2xl sm:text-3xl md:text-5xl text-white leading-tight"
              >
                {active.name}
              </h3>
              <p className="mt-3 sm:mt-4 text-white/75 max-w-xl text-sm md:text-base leading-relaxed">
                {active.description}
              </p>

              <div className="mt-5 sm:mt-7 flex items-center gap-2">
                {s.landmarks.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`landmark-dot-${i}`}
                    onClick={() => setIdx(i)}
                    aria-label={`go ${i}`}
                    className={`h-[3px] transition-all duration-500 ${
                      i === idx
                        ? "w-8 sm:w-10 bg-white"
                        : "w-4 sm:w-5 bg-white/20 hover:bg-white/35"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Weather Budapest */}
          <div
            data-testid="smart-weather-card"
            className="md:col-span-2 rounded-3xl bg-[#f5effb] border border-white/5 p-6 md:p-8 flex flex-col"
          >
            <div className="flex items-center justify-between mb-5">
              <p className="text-eyebrow">{s.weatherLabel}</p>
              <Sun size={18} className="text-[#0a0a0a]" />
            </div>
            <div>
              <h3 className="font-serif-display text-2xl text-[#0a0a0a]">
                {s.weatherCity}
              </h3>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-serif-display text-6xl gold-text">
                  {s.weatherTemp}°
                </span>
                <span className="text-sm text-[#8a7f8f]">{s.weatherDesc}</span>
              </div>
              <p className="mt-2 text-xs text-[#8a7f8f]">{s.weatherToday}</p>
            </div>

            <div className="mt-auto pt-6 grid grid-cols-3 gap-2">
              {s.forecast.map((x, i) => (
                <div
                  key={i}
                  className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/5 text-center"
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#8a7f8f]">
                    {x.d}
                  </div>
                  <div className="text-sm text-[#1a1a1a] mt-1">{x.t}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact card */}
          <div
            data-testid="smart-contact-card"
            className="md:col-span-6 rounded-3xl bg-gradient-to-br from-[#f5effb] to-[#ebe2f5] border border-white/5 p-6 sm:p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8"
          >
            <div className="max-w-xl">
              <p className="text-eyebrow">{s.contactEyebrow}</p>
              <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#0a0a0a]">
                {s.contactTitle}
              </h3>
              <p className="mt-3 text-[#8a7f8f] text-sm leading-relaxed">
                {s.contactSub}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:shrink-0">
              <a
                data-testid="contact-phone"
                href="tel:+36306278654"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/[0.03] border border-white/10 text-[#1a1a1a] hover:border-[#0a0a0a]/40 transition-colors text-sm"
              >
                <PhoneCall size={15} />
                {s.phoneCta}
              </a>
              <button
                data-testid="contact-callback"
                onClick={onCallbackClick}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full btn-outline-cream text-sm"
              >
                {s.callbackCta}
              </button>
              <button
                data-testid="contact-quote"
                onClick={onQuoteClick}
                className="btn-gold px-5 py-3 rounded-full text-sm"
              >
                {s.quoteCta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
